import React from 'react';
import PropTypes from 'prop-types';
import {
  LuArrowDown,
  LuArrowUp,
  LuArrowUpDown,
  LuBriefcase,
  LuChartColumn,
  LuCircleCheck,
  LuClipboardList,
  LuFileSpreadsheet,
  LuFolderKanban,
  LuHourglass,
  LuMedal,
  LuStar,
  LuTrophy,
  LuUsers,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { CONTROL_SURFACE, TextInput } from '../../../components/form/FormControls';
import { useTranslation } from '../../../i18n/LanguageContext';
import { EmptyState, HelpNote, LoadingRows, ProgressBar } from '../../ProfilePage/components/ProfileUi';
import CollapsibleCard from './CollapsibleCard';
import EmployeeStatsDialog from './EmployeeStatsDialog';
import { initialsOf, rateTone } from './personnelUtils';

const PERIODS = ['week', 'month', 'year', 'custom'];

const COLUMNS = [
  { key: 'employeeName', filter: 'employeeName', placeholderKey: 'searchName' },
  { key: 'totalVisits', filter: 'totalVisits', placeholder: '>5' },
  { key: 'completedVisits', filter: 'completedVisits', placeholder: '>3' },
  { key: 'totalJobs', filter: 'totalJobs', placeholder: '>0' },
  { key: 'completedJobs', filter: 'completedJobs', placeholder: '>=1' },
  { key: 'projectsWorkedOn', filter: 'projectsWorkedOn', placeholder: '>2' },
  { key: 'completionRate', filter: 'completionRate', placeholder: '>50' },
];

const RANK_STYLES = [
  { icon: LuTrophy, tone: 'tw-bg-amber-400/20 tw-text-amber-600 dark:tw-text-amber-300' },
  { icon: LuMedal, tone: 'tw-bg-slate-400/20 tw-text-slate-600 dark:tw-text-slate-300' },
  { icon: LuStar, tone: 'tw-bg-orange-400/20 tw-text-orange-600 dark:tw-text-orange-300' },
];

/**
 * "Personel Raporları": every active employee's visits and jobs in a period —
 * totals, the top three, a sortable/filterable table (filters accept 5, >5,
 * <5, >=5, <=5) and a detail window per employee. Excel export.
 */
const PersonnelReports = ({ open, onToggle, isLoadingAllData, allDataLoaded, activeCount, report, getProjectName }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`operations.personnel.${key}`, vars);
  const {
    reportPeriod,
    setReportPeriod,
    customReportStartDate,
    setCustomReportStartDate,
    customReportEndDate,
    setCustomReportEndDate,
    sortField,
    sortDirection,
    handleSort,
    reportFilters,
    handleReportFilterChange,
    personnelStatistics,
    filteredPersonnelEmployees,
    selectedEmployeeStats,
    showEmployeeModal,
    setShowEmployeeModal,
    handleEmployeeClick,
    handleExportPersonnelReport,
  } = report;
  const { totals } = personnelStatistics;

  const summaryTiles = [
    { key: 'totalVisits', value: totals.totalVisits, icon: LuClipboardList, tone: 'tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300' },
    { key: 'completedVisits', value: totals.completedVisits, icon: LuCircleCheck, tone: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' },
    { key: 'inProgressVisits', value: totals.inProgressVisits, icon: LuHourglass, tone: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300' },
    { key: 'totalJobs', value: totals.totalJobs, icon: LuBriefcase, tone: 'tw-bg-indigo-500/10 tw-text-indigo-600 dark:tw-text-indigo-300' },
    { key: 'completedJobs', value: totals.completedJobs, icon: LuCircleCheck, tone: 'tw-bg-teal-500/10 tw-text-teal-600 dark:tw-text-teal-300' },
    { key: 'uniqueProjects', value: totals.uniqueProjects, icon: LuFolderKanban, tone: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300' },
  ];

  const sortIcon = (field) => {
    if (sortField !== field) return <LuArrowUpDown aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-opacity-40" />;
    return sortDirection === 'asc'
      ? <LuArrowUp aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />
      : <LuArrowDown aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-teal-600 dark:tw-text-teal-300" />;
  };
  const ariaSort = (field) => {
    if (sortField !== field) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };
  const range = `${personnelStatistics.filterStartDate.toLocaleDateString(locale)} – ${personnelStatistics.filterEndDate.toLocaleDateString(locale)}`;
  // Sabit isim sütunu, tablonun düz zeminiyle aynı renkte
  const stickyCell = 'tw-sticky tw-left-0 tw-z-10 tw-bg-white dark:tw-bg-[#262a33]';
  const stickyHead = 'tw-sticky tw-left-0 tw-z-10 tw-bg-slate-50 dark:tw-bg-[#2c3039]';

  return (
    <>
      <CollapsibleCard
        icon={LuChartColumn}
        gradient="tw-from-sky-400 tw-to-indigo-600 tw-shadow-sky-500/30"
        title={label('title')}
        description={label('description')}
        badge={<Badge tone="info">{label('activeEmployees', { count: allDataLoaded ? totals.activeEmployees : activeCount })}</Badge>}
        open={open}
        onToggle={onToggle}
      >
        {isLoadingAllData && !allDataLoaded && <LoadingRows count={4} height="tw-h-16" label={label('loading')} />}

        {allDataLoaded && (
          <>
            {/* Dönem ve Excel */}
            <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <div className="tw-max-w-full tw-overflow-x-auto tw-pb-0.5">
                  <SegmentedControl
                    size="sm"
                    layoutId="operations-report-period"
                    label={label('period')}
                    value={reportPeriod}
                    onChange={setReportPeriod}
                    options={PERIODS.map((period) => ({ value: period, label: label(`periods.${period}`) }))}
                  />
                </div>
                {reportPeriod === 'custom' && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-block tw-w-40">
                      <TextInput type="date" aria-label={label('customStart')} value={customReportStartDate} onChange={(e) => setCustomReportStartDate(e.target.value)} />
                    </span>
                    <span aria-hidden="true" className="tw-text-slate-400">–</span>
                    <span className="tw-block tw-w-40">
                      <TextInput type="date" aria-label={label('customEnd')} value={customReportEndDate} onChange={(e) => setCustomReportEndDate(e.target.value)} />
                    </span>
                  </div>
                )}
                <span className="tw-text-xs tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{range}</span>
              </div>
              <Button size="sm" onClick={handleExportPersonnelReport} title={label('excelHint')} className="tw-self-start lg:tw-self-auto">
                <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
                {label('excel')}
              </Button>
            </div>

            {/* Özet */}
            <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-3 xl:tw-grid-cols-6">
              {summaryTiles.map((tile) => {
                const Icon = tile.icon;
                return (
                  <div key={tile.key} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tile.tone}`}>
                      <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
                    </span>
                    <div className="tw-min-w-0">
                      <dd className="tw-text-xl tw-font-semibold tw-leading-none tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{tile.value}</dd>
                      <dt className="tw-mt-1 tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label(`summary.${tile.key}`)}</dt>
                    </div>
                  </div>
                );
              })}
            </dl>

            {/* En çok ziyaret yapanlar */}
            {personnelStatistics.topByVisits.length > 0 && (
              <div className="tw-space-y-2">
                <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                  <LuTrophy aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-amber-500" />
                  {label('topTitle')}
                </h3>
                <ol className="tw-grid tw-gap-2 sm:tw-grid-cols-3">
                  {personnelStatistics.topByVisits.map((performer, index) => {
                    const RankIcon = RANK_STYLES[index].icon;
                    return (
                      <li key={performer.employeeId} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                        <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full ${RANK_STYLES[index].tone}`}>
                          <RankIcon aria-hidden="true" className="tw-h-5 tw-w-5" />
                          <span className="tw-sr-only">{index + 1}</span>
                        </span>
                        <div className="tw-min-w-0">
                          <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{performer.employeeName}</p>
                          <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                            {label('visitsShort', { count: performer.totalVisits })} · {label('rateShort', { rate: performer.completionRate })}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            <HelpNote>{label('tableHelp')}</HelpNote>

            {/* Çalışan tablosu */}
            <div className="tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white glass-scrollbar dark:tw-border-white/10 dark:tw-bg-[#262a33]">
              <table className="tw-w-full tw-min-w-[52rem] tw-border-collapse tw-text-left tw-text-sm">
                <thead>
                  <tr className="tw-bg-slate-50 dark:tw-bg-[#2c3039]">
                    {COLUMNS.map((column, index) => (
                      <th
                        key={column.key}
                        scope="col"
                        aria-sort={ariaSort(column.key)}
                        className={`tw-p-0 ${index === 0 ? `${stickyHead} tw-min-w-[14rem]` : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => handleSort(column.key)}
                          className={`tw-flex tw-w-full tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 hover:tw-text-slate-800 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-400 dark:hover:tw-text-white ${index === 0 ? '' : 'tw-justify-end'}`}
                        >
                          {label(`columns.${column.key}`)}
                          {sortIcon(column.key)}
                        </button>
                      </th>
                    ))}
                  </tr>
                  <tr className="tw-border-b tw-border-slate-200/80 tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-[#2c3039]">
                    {COLUMNS.map((column, index) => (
                      <th key={column.key} scope="col" className={`tw-px-2 tw-pb-2 tw-pt-0 tw-font-normal ${index === 0 ? stickyHead : ''}`}>
                        <input
                          type="text"
                          value={reportFilters[column.filter]}
                          onChange={(e) => handleReportFilterChange(column.filter, e.target.value)}
                          placeholder={column.placeholderKey ? label(column.placeholderKey) : label('example', { value: column.placeholder })}
                          aria-label={label('filterLabel', { column: label(`columns.${column.key}`) })}
                          className={`tw-h-8 tw-px-2.5 ${CONTROL_SURFACE} ${index === 0 ? '' : 'tw-text-right'}`}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="tw-divide-y tw-divide-slate-200/70 dark:tw-divide-white/[0.06]">
                  {filteredPersonnelEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS.length} className="tw-p-0">
                        <EmptyState compact icon={LuUsers} title={label('empty')} />
                      </td>
                    </tr>
                  ) : (
                    filteredPersonnelEmployees.map((stat) => (
                      <tr
                        key={stat.employeeId}
                        onClick={() => handleEmployeeClick(stat)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleEmployeeClick(stat);
                          }
                        }}
                        tabIndex={0}
                        aria-label={label('openEmployee', { name: stat.employeeName })}
                        className="tw-group tw-cursor-pointer tw-transition-colors hover:tw-bg-teal-500/[0.04] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-teal-500/50"
                      >
                        <td className={`${stickyCell} tw-px-3 tw-py-2.5 group-hover:tw-bg-slate-50 dark:group-hover:tw-bg-[#2a2f38]`}>
                          <span className="tw-flex tw-items-center tw-gap-2.5">
                            <span aria-hidden="true" className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-[11px] tw-font-semibold tw-text-white">
                              {initialsOf(stat.employeeName)}
                            </span>
                            <span className="tw-truncate tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{stat.employeeName}</span>
                          </span>
                        </td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{stat.totalVisits}</td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-right"><Badge tone="success">{stat.completedVisits}</Badge></td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-right tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{stat.totalJobs}</td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-right"><Badge tone="success">{stat.completedJobs}</Badge></td>
                        <td className="tw-px-3 tw-py-2.5 tw-text-right tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{stat.projectsWorkedOn}</td>
                        <td className="tw-px-3 tw-py-2.5">
                          <span className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                            <ProgressBar percent={stat.completionRate} tone={rateTone(stat.completionRate)} className="tw-w-20" />
                            <span className="tw-w-10 tw-text-right tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-700 dark:tw-text-slate-200">{stat.completionRate}%</span>
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CollapsibleCard>

      <EmployeeStatsDialog
        isOpen={showEmployeeModal && Boolean(selectedEmployeeStats)}
        stats={selectedEmployeeStats}
        range={range}
        getProjectName={getProjectName}
        onClose={() => setShowEmployeeModal(false)}
      />
    </>
  );
};

PersonnelReports.propTypes = {
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  isLoadingAllData: PropTypes.bool,
  allDataLoaded: PropTypes.bool,
  activeCount: PropTypes.number.isRequired,
  report: PropTypes.object.isRequired,
  getProjectName: PropTypes.func.isRequired,
};

export default PersonnelReports;
